import Vue from 'vue';

import * as constants from '../../constants';

import { formatPrice } from '../../helpers/format';
import { getProduct, getVariantById } from '../../helpers/shopify';

/**
 * Checks if component is filled.
 */
function isFilled(component) {
    if (component.custom['widget-type'] === 'personalization') {
        return (component.custom['personalization-text'] || '').length > 0;
    }

    if (component.custom['widget-type'] === 'dynamic-image') {
        return (component.custom['dynamic-image-url'] || '').length > 0;
    }

    return !component.placeholder;
}

/**
 * Extacts product id from a component.
 *
 * If product ID contains multiple dashes, it means there could be multiple
 * components being mapped to the same SFCC backing product.
 * Extract real product ID.
 */
function getProductId(rootState, controller, component) {
    return component.code;
}

// Price cache.
const pricesCache = {};

export default {
    state() {
        return {
            // Widget configuration.
            options: {},

            // Contains loaded product data: inventory levels, pricing, etc.
            products: null,

            // Personalization pricing product.
            personalizationProduct: null,

            // Special personalization products.
            personalizationProductByPosition: {},
        };
    },

    getters: {
        defaultUnitPrice(state, getters, rootState) {
            const controller = rootState.customizer.viewController;

            if (controller == null) {
                return 0;
            }

            const selected = controller.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

            if (selected == null) {
                return [];
            }

            const pricing = state.products.find((p) => p.productId === selected.placementIndependentCode);

            return pricing.price;
        },

        defaultUnitPriceFormatted(state, getters) {
            const price = getters.defaultUnitPrice;

            return price ? formatPrice(price) : '';
        },

        priceTiers(state, getters, rootState) {
            const controller = rootState.customizer.viewController;

            if (controller == null) {
                return [];
            }

            const selected = controller.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

            if (selected == null) {
                return [];
            }

            const pricing = state.products.find((p) => p.productId === selected.placementIndependentCode);

            if (pricing == null) {
                return [];
            }

            if (pricing.tiers == null) {
                return [];
            }

            return pricing.tiers.map((p) => ({
                quantity: parseInt(p.quantity, 10),
                formatted: p.formatted,
                amount: p.amount,
            }));
        },
    },

    mutations: {
        /**
         * Saves options to state.
         */
        SET_OPTIONS(state, options) {
            state.options = options;
        },

        /**
         * Saves product data.
         */
        SET_PRODUCTS(state, products) {
            state.products = products;
        },

        /**
         * Saves product data.
         */
        SET_PERSONALIZATION_PRODUCT(state, product) {
            state.personalizationProduct = product;
        },

        /**
         * Saves product data.
         */
        SET_PERSONALIZATION_PRODUCT_BY_POSITION(state, product) {
            state.personalizationProductByPosition[product.index] = product.product;
        },

        SET_ALWAYS_AVAILABLE(state) {
            state.options.alwaysAvailable = true;
        },
    },

    actions: {
        /**
         * Saves options.
         */
        setEcommerceOptions({ commit }, options) {
            commit('SET_OPTIONS', options);
        },

        /**
         * Loads prices for components mentioned in the blueprint.
         */
        async getPrices({
            commit,
            state,
            rootState,
        }, {
            controller,
        }) {
            const products = [];

            // Add variants.
            const productGroup = controller.blueprint.groups.find((group) => group.code === constants.GROUP_PRODUCT);
            if (productGroup != null) {
                productGroup.placements.forEach((p) => {
                    p.components.forEach((c) => {
                        if (c.custom?.['v-sku'] ?? c.placementIndependentCode) {
                            products.push({
                                productId: c.custom?.['v-sku'] ?? c.placementIndependentCode,
                                variant: null,
                                online: state.options.alwaysAvailable,
                                price: state.options.alwaysAvailable ? 10 : 0,
                                ats: state.options.alwaysAvailable ? 10 : 0,
                            });
                        }
                    });
                });
            }

            const variants = await getProduct(controller.blueprint, controller.blueprint.custom['v-handle']);

            variants.forEach((v) => {
                const exists = products.find((x) => x.productId === v.sku);

                if (exists) {
                    exists.online = true;
                    exists.price = v.price;
                    exists.variant = v.variant;

                    if (state.options.alwaysAvailable) {
                        exists.ats = 10;
                    } else {
                        exists.ats = v.ats;
                    }
                }
            });

            commit('SET_PRODUCTS', products);

            // Get customization product.
            if (controller.blueprint.custom?.['v-personalization-product']) {
                const personalizationVariant = controller.blueprint.custom?.['v-personalization-product'].split(',').map((x) => x.trim()).filter((x) => x.length > 0);

                if (personalizationVariant.length > 0) {
                    const personalizationVariants = await getVariantById(controller.blueprint, personalizationVariant[0], personalizationVariant[1]);
                    if (personalizationVariants.length > 0) {
                        commit('SET_PERSONALIZATION_PRODUCT', personalizationVariants[0]);
                    }
                }
            }

            if (controller.blueprint.custom?.['v-personalization-product-1']) {
                const personalizationVariant = controller.blueprint.custom?.['v-personalization-product-1'].split(',').map((x) => x.trim()).filter((x) => x.length > 0);

                if (personalizationVariant.length > 0) {
                    const personalizationVariants = await getVariantById(controller.blueprint, personalizationVariant[0], personalizationVariant[1]);
                    if (personalizationVariants.length > 0) {
                        commit('SET_PERSONALIZATION_PRODUCT_BY_POSITION', {
                            index: 0,
                            product: personalizationVariants[0],
                        });
                    }
                }
            }
        },

        /**
         * Gets a product/component price.
         */
        getPrice({ state }, productId) {
            const product = state.products.find((p) => p.productId === productId);
            if (product && product.online) {
                return product.price;
            }

            return null;
        },

        /**
         * Gets a product/component price.
         */
        getVariant({ state }, productId) {
            const product = state.products.find((p) => p.productId === productId);
            if (product && product.online) {
                return product.variant;
            }

            return null;
        },

        /**
         * Gets a product/component available stock.
         */
        getAts({ state }, productId) {
            const product = state.products.find((p) => p.productId === productId);
            if (product && product.online) {
                return product.ats;
            }

            return 0;
        },

        /**
         * Attaches necessary state related methods to the controller and triggers the loading status.
         */
        loadController({
            rootState,
            state,
            dispatch,
            commit,
        }, controller) {
            controller.on('setReactive', (v) => {
                Vue.set(v.target, v.key, v.value);

                dispatch('enforceMirroring', controller);

                return true;
            });

            controller.on('stateChanged', () => {
                dispatch('enforceMirroring', controller);
            });

            controller.on('initializeAvailability', ({ blueprint }) => {
                if (blueprint.custom['v-always-available'] === 'true') {
                    commit('SET_ALWAYS_AVAILABLE');
                }

                return dispatch('getPrices', {
                    controller,
                }).then((data) => data);
            });

            controller.on('afterInitializeState', () => {
                // Assign placement numbers.
                let nextLayer = 2;

                controller.state.forEach((s) => {
                    if (s.placement.code === constants.PLACEMENT_PRODUCT) {
                        controller.commitCustomPlacementAttributes(s.placement.code, {
                            'explicit-order': 0,
                        });
                    } else if (s.placement.code.startsWith(constants.PLACEMENT_EMBELLISHMENTS_BASE)) {
                        controller.commitCustomPlacementAttributes(s.placement.code, {
                            'explicit-order': 1,
                        });
                    } else if (s.placement.code.startsWith(constants.PLACEMENT_EMBELLISHMENTS)) {
                        controller.commitCustomPlacementAttributes(s.placement.code, {
                            'explicit-order': 999999,
                        });
                    } else {
                        controller.commitCustomPlacementAttributes(s.placement.code, {
                            'explicit-order': nextLayer,
                        });

                        nextLayer += 1;
                    }
                });
            });

            controller.on('isAvailable', (component) => {
                if (state.options.alwaysAvailable) {
                    return true;
                }

                let productId = getProductId(rootState, controller, component);

                if (productId.startsWith(constants.COMPONENT_PRODUCT)) {
                    productId = productId.substr(constants.COMPONENT_PRODUCT.length);

                    const product = state.products.find((p) => p.productId === productId);
                    if (product) {
                        return product.online && product.ats > 0;
                    }

                    return false;
                }

                return true;
            });

            controller.on('getPrice', (component) => {
                if (isFilled(component)) {
                    let productId = getProductId(rootState, controller, component);

                    if (productId.startsWith(constants.COMPONENT_PRODUCT)) {
                        productId = productId.substr(constants.COMPONENT_PRODUCT.length);

                        const product = state.products.find((p) => p.productId === productId);
                        if (product && product.online) {
                            return product.price;
                        }
                    }
                }

                return null;
            });

            controller.load();
        },

        /**
         * Calculates full state price.
         */
        getStatePrice(_, { controller, options }) {
            const finalOptions = {
                ...{
                    ignoreOptional: true,

                    fixedPlacements: ['Product'],
                },
                ...options,
            };

            const price = controller.statePrice(finalOptions);

            return price;
        },

        /**
         * Formats price on the server using current locale settings.
         */
        formatPrice({ state }, price) {
            if (pricesCache[price]) {
                return Promise.resolve(pricesCache[price]);
            }

            const formatted = formatPrice(price);

            pricesCache[price] = formatted;

            return Promise.resolve(formatted);
        },
    },
};
